import {createApi} from '@reduxjs/toolkit/query/react';
import {IUser, IUserPreferences} from 'models/users';
import {IListPayload, IListQuery} from 'models/generic';
import {baseQueryWithAuth} from 'features/baseQueryWithAuth/baseQueryWithAuth';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: ['Users', 'User', 'UserPreferences'],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    me: builder.query<{active: boolean; permissions: string[]; regions: string[]; roles: []}, void>({
      query: () => ({
        url: `users/me`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    find: builder.query<IListPayload<IUser>, IListQuery>({
      query: ({offset = 0, limit = 10, sort = '_id:dsc', search = ''}) => ({
        url: `users?offset=${offset}&limit=${limit}&sort=${sort}${search && search.length ? '&search=' + search : ''}`,
        method: 'GET',
      }),
      providesTags: [{type: 'Users', id: 'LIST'}],
    }),
    findById: builder.query<IUser, string>({
      query: (id: string) => ({
        url: `users/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{type: 'Users', id}],
    }),
    insert: builder.mutation<IUser, Partial<IUser>>({
      query: user => ({
        url: `users`,
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [{type: 'Users', id: 'LIST'}],
    }),
    update: builder.mutation<IUser, {id: string; user: Partial<IUser>}>({
      query: ({id, user}) => ({
        url: `users/${id}`,
        method: 'PUT',
        body: user,
      }),
      // Refetch only the edited record and the list; the global 'User' tag
      // (logged-in user's permissions) must not be invalidated on every edit.
      invalidatesTags: (result, error, {id}) => [
        {type: 'Users', id},
        {type: 'Users', id: 'LIST'},
      ],
    }),
    delete: builder.mutation<IUser, string>({
      query: (id: string) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{type: 'Users', id: 'LIST'}],
    }),
    preferences: builder.query<IUserPreferences<unknown>, void>({
      query: () => ({
        url: 'users/me/preferences',
        method: 'GET',
      }),
      providesTags: ['UserPreferences'],
    }),
    updatePreferences: builder.mutation<IUserPreferences<unknown>, IUserPreferences<unknown>>({
      query: prefs => ({
        url: 'users/me/preferences',
        method: 'PUT',
        body: prefs,
      }),
      invalidatesTags: ['UserPreferences'],
    }),
  }),
});

export const {
  useMeQuery,
  useFindQuery,
  useFindByIdQuery,
  useInsertMutation,
  useUpdateMutation,
  useDeleteMutation,
  usePreferencesQuery,
  useUpdatePreferencesMutation,
} = usersApi;
